/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Rows pinned under the site header glide with it.
 *
 * The comparer's filter bar, the compare grid's provider row and a fitting
 * table's header cells stick at `top: var(--header-offset)` (index.css). That
 * alone puts them in the right place in every state: in their own slot until
 * the slot reaches the header, then just under its visible bottom edge — 80 px
 * with the header in view, 0 below `lg` once it has slid away. What a variable
 * cannot do is MOVE a row: the offset changes in one step, so a stuck row
 * would jump while the header beside it slides for 300 ms.
 *
 * `setHeaderState` is the one place the offset changes (Header.tsx calls it).
 * It measures every row that opted in with `data-follows-header` before and
 * after the change and plays the difference back as a transform — FLIP, on
 * the compositor, with the header's own duration and easing. A row resting in
 * its slot measures the same twice and is left alone; a row whose slot sits
 * inside the band the header gives up glides exactly the part it moves.
 *
 * Why not what came before (both measured on a 390 × 844 phone, 2026-10-01):
 * - `top-20` + `translate-y-[calc(var(--header-offset)-5rem)]` (b14bffa): a
 *   transform moves a row whether it is stuck or not, so with the header away
 *   the filter bar sat 80 px above its own slot — over the bottom 56 px of the
 *   Stap 2 block — and jumped 80 px at every flip while it was in view.
 * - `top-[var(--header-offset)]` + `transition-[top]` (441a0db): right in
 *   every state, but a layout per frame for the length of every slide.
 *
 * A `fixed` element is always pinned, so it may keep the translate: the
 * reading-progress bars do.
 */

/** On <html>: `collapsed` while the header is out of the way (index.css). */
export const HEADER_STATE_ATTR = 'data-header-state';

/** Opt-in marker for a row that sticks at `top-[var(--header-offset)]`. */
export const FOLLOWS_HEADER_ATTR = 'data-follows-header';

/**
 * The header's own slide: Tailwind's `duration-300` with its default easing.
 * `fill: 'backwards'` holds the first keyframe until the animation has really
 * started — what a CSS transition, the header's, does by definition. Without
 * it a glide whose start lands a frame late shows the row at its NEW position
 * for that frame: measured once on the phone run, an 80 px flash.
 */
export const HEADER_GLIDE: KeyframeAnimationOptions = {
  duration: 300,
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  fill: 'backwards',
};

/** Each row's glide still in flight, cancelled when the header flips again. */
const glides = new WeakMap<Element, Animation>();

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Publish the header's state on `root` and glide every following row from
 * where it was to where the new offset puts it. A call that changes nothing
 * measures nothing. Without the Web Animations API (or with reduced motion)
 * the rows simply land: the position is the stylesheet's, the glide is polish.
 */
export const setHeaderState = (root: HTMLElement, collapsed: boolean): void => {
  if ((root.getAttribute(HEADER_STATE_ATTR) === 'collapsed') === collapsed) return;

  const rows = Array.from(root.querySelectorAll<HTMLElement>(`[${FOLLOWS_HEADER_ATTR}]`));
  // Where each row is on screen now, a glide still in flight included — so a
  // flip half way through a slide carries on from where the row really is.
  const before = rows.map((row) => row.getBoundingClientRect().top);
  for (const row of rows) glides.get(row)?.cancel();

  if (collapsed) root.setAttribute(HEADER_STATE_ATTR, 'collapsed');
  else root.removeAttribute(HEADER_STATE_ATTR);

  if (prefersReducedMotion()) return;
  rows.forEach((row, i) => {
    // The first read after the flip lays the page out with the new offset —
    // the layout the next frame needed anyway, done once instead of per frame.
    const from = before[i] - row.getBoundingClientRect().top;
    if (Math.abs(from) < 0.5 || typeof row.animate !== 'function') return;
    glides.set(
      row,
      row.animate([{ transform: `translateY(${from}px)` }, { transform: 'none' }], HEADER_GLIDE)
    );
  });
};
